import type { PropsWithChildren } from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import LoaderGradient from '../../icons/LoaderGradient'
import { cn } from '../../utils/cn'
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip'

// The base reserves a 0.5px border and every variant names its color, so swapping variants on one
// element (ghost <-> secondary tab pills) never resizes it. Same idiom as Badges.
const buttonVariants = cva(
  "inline-flex items-center cursor-pointer disabled:cursor-not-allowed justify-center gap-1 whitespace-nowrap rounded-lg border-[0.5px] transition-[color,background-color,border-color,opacity,box-shadow,filter,transform,scale] active:scale-98 disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg[width='20'][height='20']:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-[2px] aria-invalid:ring-destructive-foreground dark:aria-invalid:ring-destructive-foreground aria-invalid:border-destructive-foreground overflow-hidden",

  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground active:bg-primary/90 hover:brightness-105 active:brightness-100',
        secondary:
          'border-border bg-secondary text-foreground shadow-sm dark:shadow-none hover:bg-accent active:bg-accent disabled:border-transparent disabled:shadow-none disabled:bg-secondary aria-disabled:border-transparent aria-disabled:shadow-none aria-disabled:bg-secondary',
        outline:
          'border-border text-foreground hover:bg-muted active:bg-muted/90 hover:brightness-105 active:brightness-100',
        ghost:
          'border-transparent hover:bg-muted active:bg-muted/90 hover:brightness-105 active:brightness-100',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground active:bg-destructive/90 hover:brightness-105 active:brightness-100',
        success:
          'border-transparent bg-success text-success-foreground active:bg-success/90 hover:brightness-105 active:brightness-100',
        warning:
          'border-transparent bg-warning text-warning-foreground active:bg-warning/90 hover:brightness-105 active:brightness-100',
        info: 'border-transparent bg-info text-info-foreground active:bg-info/90 hover:brightness-105 active:brightness-100'
      },
      size: {
        default: 'h-8 px-3 py-1 text-2xs leading-none',
        xs: 'h-6 gap-1 px-2 text-2xs leading-none',
        sm: 'h-7 gap-1 px-3 text-2xs leading-none',
        md: 'h-10 px-4 text-xs leading-none',
        lg: 'h-12 px-4 text-sm leading-none',
        icon: 'size-8 p-1.5 [&_svg]:shrink-0',
        iconXs: 'size-6 p-0.5 [&_svg]:size-5 [&_svg]:shrink-0',
        iconSm:
          "size-6.5 p-0.75 [&_svg]:size-3.5 [&_svg[width='20'][height='20']]:size-5 [&_svg]:shrink-0",
        iconMd: 'size-10 p-2 [&_svg]:shrink-0',
        iconLg: 'size-12 p-3 [&_svg]:shrink-0'
      },
      fullWidth: {
        true: 'w-full',
        false: ''
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false
    }
  }
)

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    isLoading?: boolean
    /**
     * Hover/focus tooltip. When provided, the button is wrapped in a Radix
     * tooltip. A string tooltip also backfills `aria-label` when one isn't set.
     * Requires a `TooltipProvider` ancestor, which apps mount at the root (same
     * contract as using `Tooltip` directly).
     */
    tooltip?: React.ReactNode | string
  } & PropsWithChildren

function Button({
  className,
  variant,
  size,
  fullWidth,
  asChild = false,
  isLoading = false,
  disabled,
  tooltip,
  children,
  onClick,
  'aria-label': ariaLabelProp,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  const isDisabled = isLoading || disabled

  // A natively disabled button swallows pointer/focus events, so its tooltip would never
  // open — often exactly when it matters most (explaining *why* the action is
  // unavailable). With a tooltip, disabled state is conveyed via `aria-disabled` instead:
  // the button stays hoverable/focusable as a single semantic trigger, and activation is
  // guarded below.
  const usesAriaDisabled = Boolean(isDisabled && tooltip !== undefined && tooltip !== null)

  // When asChild is true, we need to handle the loading state differently
  // since the Slot component will pass children to the child component
  const buttonContent = asChild ? (
    children
  ) : (
    <>
      {isLoading && <LoaderGradient aria-label="Loading" />}
      {children}
    </>
  )

  // Backfill the accessible name from a string tooltip. `aria-label` is pulled
  // out of `props` above so the spread can't override this computed value.
  const ariaLabel = ariaLabelProp ?? (typeof tooltip === 'string' ? tooltip : undefined)

  const button = (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      disabled={usesAriaDisabled ? undefined : isDisabled}
      aria-disabled={usesAriaDisabled || undefined}
      onClick={
        usesAriaDisabled
          ? event => {
              // Mirror a natively disabled button: no activation (incl. form submission)
              // and no bubbling to parent click handlers.
              event.preventDefault()
              event.stopPropagation()
            }
          : onClick
      }
      {...props}
      aria-label={ariaLabel}
    >
      {buttonContent}
    </Comp>
  )

  if (tooltip === undefined || tooltip === null) {
    return button
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export { Button, buttonVariants }
